import type { $Fetch } from 'ofetch';
import type { OfertaGateway, DiarioAulaGateway, ListOfertasParams } from '@/application/port/out/OfertaGateways';
import type {
  Oferta, OfertaStatus, DiarioAula,
  CreateOfertaCommand, UpdateOfertaCommand, DiarioAulaCommand,
} from '@/domain/model/Oferta';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateways de Oferta + Diário de classe (enrollment).
 * Diário DELETE = 204 (sem assertApiSuccess).
 *
 * @since G.5.19
 */
export class HttpOfertaGateway implements OfertaGateway {
  private readonly base = '/api/v1/ofertas';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListOfertasParams): Promise<Page<Oferta>> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 50 };
    if (params.periodoLetivoId) query.periodoLetivoId = params.periodoLetivoId;
    if (params.disciplineId) query.disciplineId = params.disciplineId;
    if (params.status) query.status = params.status;
    const res = await this.client<ApiResponse<Page<Oferta>>>(this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }
  async create(cmd: CreateOfertaCommand): Promise<Oferta> {
    const res = await this.client<ApiResponse<Oferta>>(this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async update(id: string, cmd: UpdateOfertaCommand): Promise<Oferta> {
    const res = await this.client<ApiResponse<Oferta>>(`${this.base}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async mudarStatus(id: string, status: OfertaStatus): Promise<Oferta> {
    const res = await this.client<ApiResponse<Oferta>>(
      `${this.base}/${encodeURIComponent(id)}/status`, { method: 'PATCH', body: { status } });
    assertApiSuccess(res);
    return res.data;
  }
}

export class HttpDiarioAulaGateway implements DiarioAulaGateway {
  constructor(private readonly client: $Fetch) {}
  private base(ofertaId: string): string {
    return `/api/v1/ofertas/${encodeURIComponent(ofertaId)}/diario`;
  }

  async list(ofertaId: string, page = 0, size = 50): Promise<Page<DiarioAula>> {
    const res = await this.client<ApiResponse<Page<DiarioAula>>>(
      this.base(ofertaId), { method: 'GET', params: { page, size } });
    assertApiSuccess(res);
    return res.data;
  }
  async registrar(ofertaId: string, cmd: DiarioAulaCommand): Promise<DiarioAula> {
    const res = await this.client<ApiResponse<DiarioAula>>(this.base(ofertaId), { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async atualizar(ofertaId: string, id: string, cmd: DiarioAulaCommand): Promise<DiarioAula> {
    const res = await this.client<ApiResponse<DiarioAula>>(
      `${this.base(ofertaId)}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }
  async excluir(ofertaId: string, id: string): Promise<void> {
    // DELETE 204 No Content (ApiResponse.noContent() no BE).
    await this.client(`${this.base(ofertaId)}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
